import { useState } from "react";
import { useFetch } from "@/hooks/useFetch";
import { usePermissions } from "@/lib/permissions";
import { maintenanceService, documentService } from "@/services/api";
import { PageHeader } from "@/components/common/PageHeader";
import { KPICard } from "@/components/common/KPICard";
import { EmptyState } from "@/components/common/EmptyState";
import { SkeletonTable } from "@/components/common/Skeletons";
import { AddMaintenanceDialog } from "@/components/dialogs/AddMaintenanceDialog";
import { AddDocumentDialog } from "@/components/dialogs/AddDocumentDialog";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Wrench, FileWarning, Plus, Check, AlertTriangle } from "lucide-react";

export default function Maintenance() {
  const { isManager } = usePermissions();
  const [addScheduleOpen, setAddScheduleOpen] = useState(false);
  const [addDocOpen, setAddDocOpen] = useState(false);
  const [loggingId, setLoggingId] = useState(null);

  const { data: due, loading: dueLoading, refetch: refetchDue } = useFetch(() => maintenanceService.due(), []);
  const { data: expiring, loading: expiringLoading, refetch: refetchExpiring } = useFetch(() => documentService.expiring(), []);
  const { data: schedules, loading: schedulesLoading, refetch: refetchSchedules } = useFetch(() => maintenanceService.list(), []);

  const overdueCount = (due || []).filter((d) => d.status === "overdue").length;

  const refetchAll = () => { refetchDue(); refetchExpiring(); refetchSchedules(); };

  const logService = async (scheduleId) => {
    setLoggingId(scheduleId);
    try {
      await maintenanceService.logService(scheduleId);
      toast.success("Service logged — next due date recalculated");
      refetchAll();
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not log service");
    } finally {
      setLoggingId(null);
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <PageHeader
        title="Maintenance"
        subtitle="Service schedules and document expiry, tracked per vehicle."
        actions={isManager && (
          <>
            <Button variant="secondary" onClick={() => setAddDocOpen(true)}><Plus className="mr-1.5 h-4 w-4" /> Add Document</Button>
            <Button onClick={() => setAddScheduleOpen(true)} data-testid="add-schedule"><Plus className="mr-1.5 h-4 w-4" /> Add Schedule</Button>
          </>
        )}
      />

      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        <KPICard label="Due or Overdue" value={(due || []).length} icon={Wrench} accent={(due || []).length > 0} />
        <KPICard label="Overdue" value={overdueCount} icon={AlertTriangle} accent={overdueCount > 0} />
        <KPICard label="Documents Expiring" value={(expiring || []).length} icon={FileWarning} accent={(expiring || []).length > 0} />
        <KPICard label="Active Schedules" value={(schedules || []).length} icon={Check} />
      </div>

      <div className="space-y-6">
        <div>
          <h3 className="mb-3 font-display text-base font-medium">Maintenance due</h3>
          {dueLoading ? <SkeletonTable rows={3} /> : !due || due.length === 0 ? (
            <EmptyState icon={Check} title="Nothing due" description="All maintenance schedules are up to date." />
          ) : (
            <div className="overflow-hidden rounded-2xl border border-border/70 bg-card/40">
              <Table>
                <TableHeader><TableRow className="hover:bg-transparent border-border/60"><TableHead>Vehicle</TableHead><TableHead>Service</TableHead><TableHead>Status</TableHead><TableHead>Details</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {due.map((d) => (
                    <TableRow key={d.schedule_id} className="border-border/50" data-testid={`maintenance-due-${d.schedule_id}`}>
                      <TableCell className="font-medium">{d.vehicle}</TableCell>
                      <TableCell className="capitalize">{d.service_type.replace(/_/g, " ")}</TableCell>
                      <TableCell>
                        <span className={cn("rounded-full border px-2.5 py-1 text-xs font-medium", d.status === "overdue" ? "border-red-500/25 bg-red-500/10 text-red-700 dark:text-red-300" : "border-amber-400/20 bg-amber-400/10 text-amber-700 dark:text-amber-300")}>
                          {d.status === "overdue" ? "Overdue" : "Due soon"}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{d.reasons.join(", ")}</TableCell>
                      <TableCell className="text-right">
                        {isManager && (
                          <Button size="sm" variant="secondary" disabled={loggingId === d.schedule_id} onClick={() => logService(d.schedule_id)} data-testid={`log-service-${d.schedule_id}`}>
                            {loggingId === d.schedule_id ? "Logging…" : "Log Service"}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        <div>
          <h3 className="mb-3 font-display text-base font-medium">Documents expiring</h3>
          {expiringLoading ? <SkeletonTable rows={3} /> : !expiring || expiring.length === 0 ? (
            <EmptyState icon={Check} title="Nothing expiring" description="No documents due for renewal in the next 30 days." />
          ) : (
            <div className="overflow-hidden rounded-2xl border border-border/70 bg-card/40">
              <Table>
                <TableHeader><TableRow className="hover:bg-transparent border-border/60"><TableHead>Vehicle</TableHead><TableHead>Document</TableHead><TableHead>Expiry</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {expiring.map((doc) => (
                    <TableRow key={doc.document_id} className="border-border/50">
                      <TableCell className="font-medium">{doc.vehicle}</TableCell>
                      <TableCell className="capitalize">{doc.doc_type}</TableCell>
                      <TableCell>{doc.expiry_date}</TableCell>
                      <TableCell>
                        <span className={cn("rounded-full border px-2.5 py-1 text-xs font-medium", doc.status === "expired" ? "border-red-500/25 bg-red-500/10 text-red-700 dark:text-red-300" : "border-amber-400/20 bg-amber-400/10 text-amber-700 dark:text-amber-300")}>
                          {doc.status === "expired" ? `Expired ${Math.abs(doc.days_remaining)}d ago` : `${doc.days_remaining}d remaining`}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        <div>
          <h3 className="mb-3 font-display text-base font-medium">All schedules</h3>
          {schedulesLoading ? <SkeletonTable rows={3} /> : !schedules || schedules.length === 0 ? (
            <p className="text-sm text-muted-foreground">No maintenance schedules yet.</p>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-border/70 bg-card/40">
              <Table>
                <TableHeader><TableRow className="hover:bg-transparent border-border/60"><TableHead>Vehicle ID</TableHead><TableHead>Service</TableHead><TableHead>Interval</TableHead><TableHead>Last Service</TableHead><TableHead>Next Due</TableHead></TableRow></TableHeader>
                <TableBody>
                  {schedules.map((s) => (
                    <TableRow key={s.id} className="border-border/50">
                      <TableCell className="font-telemetry text-xs text-muted-foreground">{s.vehicle_id.slice(0, 8)}…</TableCell>
                      <TableCell className="capitalize">{s.service_type.replace(/_/g, " ")}</TableCell>
                      <TableCell>{[s.interval_km && `${s.interval_km} mi`, s.interval_days && `${s.interval_days} days`].filter(Boolean).join(" / ") || "—"}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{s.last_service_at ? s.last_service_at.slice(0, 10) : "Never"}</TableCell>
                      <TableCell className="text-sm">{[s.next_due_odometer && `${s.next_due_odometer.toFixed(0)} mi`, s.next_due_at && s.next_due_at.slice(0, 10)].filter(Boolean).join(" / ") || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>

      {isManager && (
        <>
          <AddMaintenanceDialog open={addScheduleOpen} onOpenChange={setAddScheduleOpen} onSaved={refetchAll} />
          <AddDocumentDialog open={addDocOpen} onOpenChange={setAddDocOpen} onSaved={refetchAll} />
        </>
      )}
    </div>
  );
}
